import type { Bbox2 } from "../geo/bbox";
import { drawAnnotations, type Annotation } from "./Annotation";
import type { OrthoStack } from "./OrthoStack";
import { compositeStack } from "./PlanRenderer";

export type PaperSize = "A4" | "A3" | "A2" | "A1" | "A0";
export type Orientation = "portrait" | "landscape";

/** Everything about a print job the sidebar lets the user choose. */
export interface PrintUiState {
  paper: PaperSize;
  orientation: Orientation;
  /** The N in a 1:N map scale. */
  scaleDenominator: number;
  dpi: number;
}

export interface PrintOptions extends PrintUiState {
  /** World (CRS) point the printed page is centred on — the current view's centre; not a
   *  user-editable field in its own right, so it's kept out of `PrintUiState`. */
  center: { x: number; y: number };
}

const PAPER_MM: Record<PaperSize, [number, number]> = {
  A4: [210, 297],
  A3: [297, 420],
  A2: [420, 594],
  A1: [594, 841],
  A0: [841, 1189],
};

/** Common architectural/civil print scales — shown as presets, a custom value is also accepted. */
export const SCALE_PRESETS = [100, 200, 250, 500, 1000, 2000, 5000, 10000];
export const DPI_PRESETS = [150, 300];

const MARGIN_MM = 14;
const TITLE_STRIP_MM = 10;

function pageSizeMm(options: Pick<PrintOptions, "paper" | "orientation">): [number, number] {
  const [w, h] = PAPER_MM[options.paper];
  return options.orientation === "landscape" ? [h, w] : [w, h];
}

/**
 * Renders one print page to a canvas at the requested scale/paper/DPI: the raster stack
 * composited at whatever real-world extent that combination implies (see the scale-to-extent
 * maths below), a frame, a scale bar and a title strip, with any annotations on top — all at
 * export resolution, not scaled up from the screen preview.
 */
export async function renderPlanPage(
  stack: OrthoStack,
  annotations: Annotation[],
  options: PrintOptions,
  signal?: AbortSignal,
): Promise<HTMLCanvasElement> {
  const [pageWMm, pageHMm] = pageSizeMm(options);
  const printableWMm = pageWMm - 2 * MARGIN_MM;
  const printableHMm = pageHMm - 2 * MARGIN_MM - TITLE_STRIP_MM;

  // A 1:N scale means N mm of the real world per mm of paper — so the map's real-world
  // extent is just the printable area's physical size times N.
  const worldW = (printableWMm / 1000) * options.scaleDenominator;
  const worldH = (printableHMm / 1000) * options.scaleDenominator;
  const extent: Bbox2 = {
    minX: options.center.x - worldW / 2,
    maxX: options.center.x + worldW / 2,
    minY: options.center.y - worldH / 2,
    maxY: options.center.y + worldH / 2,
  };

  const pxPerMm = options.dpi / 25.4;
  const pageWPx = Math.round(pageWMm * pxPerMm);
  const pageHPx = Math.round(pageHMm * pxPerMm);
  const marginPx = Math.round(MARGIN_MM * pxPerMm);
  const titleStripPx = Math.round(TITLE_STRIP_MM * pxPerMm);
  const innerWPx = pageWPx - 2 * marginPx;
  const innerHPx = pageHPx - 2 * marginPx - titleStripPx;

  const canvas = document.createElement("canvas");
  canvas.width = pageWPx;
  canvas.height = pageHPx;
  const ctx = require2d(canvas);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, pageWPx, pageHPx);

  // Composited separately from the page canvas so "where is the map" and "where is the
  // margin" never have to share one coordinate system.
  const inner = document.createElement("canvas");
  inner.width = Math.max(1, innerWPx);
  inner.height = Math.max(1, innerHPx);
  const innerCtx = require2d(inner);
  await compositeStack(stack.paintOrder(), { ctx: innerCtx, pixelWidth: inner.width, pixelHeight: inner.height, extent }, signal);
  drawAnnotations(innerCtx, annotations, extent, inner.width, inner.height);
  ctx.drawImage(inner, marginPx, marginPx);

  ctx.strokeStyle = "#333333";
  ctx.lineWidth = Math.max(1, pxPerMm * 0.25);
  ctx.strokeRect(marginPx, marginPx, innerWPx, innerHPx);

  drawScaleBar(ctx, options, extent, marginPx, innerHPx, pxPerMm);
  drawTitle(ctx, stack, options, marginPx, innerHPx, pxPerMm);

  return canvas;
}

/** Renders the page and hands back a PDF blob sized to the physical paper — printed at 100%,
 *  the scale bar and the chosen `1:N` are both literally true on paper. */
export async function exportPlanPdf(
  stack: OrthoStack,
  annotations: Annotation[],
  options: PrintOptions,
  signal?: AbortSignal,
): Promise<Blob> {
  const canvas = await renderPlanPage(stack, annotations, options, signal);
  const [pageWMm, pageHMm] = pageSizeMm(options);
  const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ unit: "mm", format: [pageWMm, pageHMm], orientation: options.orientation });
  doc.addImage(dataUrl, "JPEG", 0, 0, pageWMm, pageHMm);
  return doc.output("blob");
}

function drawScaleBar(
  ctx: CanvasRenderingContext2D,
  options: PrintOptions,
  extent: Bbox2,
  marginPx: number,
  innerHPx: number,
  pxPerMm: number,
): void {
  const worldWidth = extent.maxX - extent.minX;
  const niceLength = niceScaleLength(worldWidth / 5);
  const barWidthPx = (niceLength / options.scaleDenominator) * 1000 * pxPerMm;
  const x0 = marginPx + 6 * pxPerMm;
  const y0 = marginPx + innerHPx - 8 * pxPerMm;
  const tick = 1.6 * pxPerMm;

  ctx.strokeStyle = "#000000";
  ctx.fillStyle = "#000000";
  ctx.lineWidth = Math.max(1, pxPerMm * 0.35);
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.lineTo(x0 + barWidthPx, y0);
  for (const fraction of [0, 0.5, 1]) {
    const x = x0 + barWidthPx * fraction;
    ctx.moveTo(x, y0 - tick);
    ctx.lineTo(x, y0 + tick);
  }
  ctx.stroke();

  ctx.font = `${2.8 * pxPerMm}px sans-serif`;
  ctx.textBaseline = "bottom";
  ctx.fillText(formatLength(niceLength), x0, y0 - tick - 1);
  ctx.fillText(`1:${options.scaleDenominator}`, x0 + barWidthPx + 4 * pxPerMm, y0 + tick);
}

function drawTitle(
  ctx: CanvasRenderingContext2D,
  stack: OrthoStack,
  options: PrintOptions,
  marginPx: number,
  innerHPx: number,
  pxPerMm: number,
): void {
  const names = stack.paintOrder().map((l) => l.source.label).join(", ") || "—";
  const text = `${names}   ·   1:${options.scaleDenominator}   ·   ${new Date().toLocaleDateString()}`;
  ctx.fillStyle = "#000000";
  ctx.font = `${3 * pxPerMm}px sans-serif`;
  ctx.textBaseline = "top";
  ctx.fillText(text, marginPx, marginPx + innerHPx + 2 * pxPerMm);
}

/** Rounds to a "nice" 1/2/5×10^n length close to `target`, for a scale bar worth reading. */
function niceScaleLength(target: number): number {
  if (!(target > 0)) return 1;
  const exponent = Math.floor(Math.log10(target));
  const base = 10 ** exponent;
  const candidates = [1, 2, 5, 10].map((m) => m * base);
  return candidates.reduce((best, c) => (Math.abs(c - target) < Math.abs(best - target) ? c : best));
}

function formatLength(metres: number): string {
  return metres >= 1000 ? `${metres / 1000} km` : `${metres} m`;
}

/** Triggers a browser "Save as" for `blob` — there's no Trimble Connect destination for an
 *  export like this, it always goes straight to the user's own machine. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function require2d(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas context unavailable");
  return ctx;
}
